import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, HardDrive, Upload, Download } from 'lucide-react';

const AgentInstaller = () => {
  const navigate = useNavigate();
  const [agentFiles, setAgentFiles] = useState({ x64: null, x86: null });
  const [agentInfo, setAgentInfo] = useState({ x64: null, x86: null });
  const [uploadingAgent, setUploadingAgent] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAgentInfo();
  }, []);

  const fetchAgentInfo = async () => {
    try {
      const response = await api.get('/admin/agent-info');
      setAgentInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch agent info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAgentFileChange = (arch, file) => {
    if (file && file.name.endsWith('.exe')) {
      setAgentFiles({ ...agentFiles, [arch]: file });
    } else {
      toast.error('Please select a valid .exe file');
    }
  };

  const handleAgentUpload = async (arch) => {
    const file = agentFiles[arch];
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploadingAgent(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(`/admin/upload-agent?arch=${arch}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(`${arch.toUpperCase()} agent uploaded successfully!`);
      setAgentFiles({ ...agentFiles, [arch]: null });
      fetchAgentInfo(); // Refresh agent info
    } catch (error) {
      toast.error(`Failed to upload ${arch.toUpperCase()} agent`);
    } finally {
      setUploadingAgent(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/settings')}
              className="border-slate-300"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl flex items-center justify-center">
                <HardDrive className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Agent Installer</h1>
                <p className="text-sm text-slate-500">Upload and manage RMM agent installers</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <Card className="shadow-sm border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <HardDrive className="w-5 h-5 text-purple-600" />
              <span>RMM Agent Management</span>
            </CardTitle>
            <CardDescription>
              Upload new agent versions for deployment to client systems
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* x64 Agent Upload */}
              <div className="border border-slate-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-800">x64 Agent (64-bit)</h3>
                  {agentInfo.x64 && (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                      Available
                    </Badge>
                  )}
                </div>
                
                {agentInfo.x64 && (
                  <div className="bg-slate-50 rounded p-3 text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Size:</span>
                      <span className="font-mono">{(agentInfo.x64.size / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Last Updated:</span>
                      <span className="font-mono">{new Date(agentInfo.x64.modified).toLocaleString()}</span>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="agent-x64-upload">Upload New x64 Agent</Label>
                  <Input
                    id="agent-x64-upload"
                    type="file"
                    accept=".exe"
                    onChange={(e) => handleAgentFileChange('x64', e.target.files[0])}
                    className="border-slate-300"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleAgentUpload('x64')}
                    disabled={!agentFiles.x64 || uploadingAgent}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingAgent ? 'Uploading...' : 'Upload x64'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/public/agent-download?arch=x64`, '_blank')}
                    disabled={!agentInfo.x64}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>

              {/* x86 Agent Upload */}
              <div className="border border-slate-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-800">x86 Agent (32-bit)</h3>
                  {agentInfo.x86 && (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                      Available
                    </Badge>
                  )}
                </div>
                
                {agentInfo.x86 && (
                  <div className="bg-slate-50 rounded p-3 text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Size:</span>
                      <span className="font-mono">{(agentInfo.x86.size / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Last Updated:</span>
                      <span className="font-mono">{new Date(agentInfo.x86.modified).toLocaleString()}</span>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="agent-x86-upload">Upload New x86 Agent</Label>
                  <Input
                    id="agent-x86-upload"
                    type="file"
                    accept=".exe"
                    onChange={(e) => handleAgentFileChange('x86', e.target.files[0])}
                    className="border-slate-300"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleAgentUpload('x86')}
                    disabled={!agentFiles.x86 || uploadingAgent}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingAgent ? 'Uploading...' : 'Upload x86'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/public/agent-download?arch=x86`, '_blank')}
                    disabled={!agentInfo.x86}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2 text-sm">Agent Upload Notes:</h4>
                <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                  <li>Uploaded agents will be immediately available for download</li>
                  <li>Existing agents will be replaced with new uploads</li>
                  <li>Files must be named RMMAgent-x64.exe or RMMAgent-x86.exe</li>
                  <li>Ensure agents are properly built and tested before uploading</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AgentInstaller;
